/**
 * Panels that are destinations, rendered as pages.
 *
 * The rail had two kinds of slideout wearing one costume. A **picker** —
 * Projects, Funding, Funders, Release Notes — lists things and navigates the
 * main area when you choose one; the page beside it is the point of the panel.
 * A **destination** — Settings, Organizations, Program Increments, Tasks — is
 * self-contained and navigates nowhere, so the main area kept whatever happened
 * to be there. Opening Settings left you editing statuses beside a table of 75
 * team members, which reads as a page that has not finished loading.
 *
 * The tell is in the components themselves: the pickers call `navigate()`, the
 * destinations never do. So the destinations become routes and the pickers stay
 * as panels.
 *
 * Docs and API Docs deliberately stay slideouts. Reading reference material
 * *beside* the work is what they are for, which is also why the docs panel is
 * the one that can be dragged wider.
 */
import type { ReactNode } from 'react';
import SettingsPanel from '../components/settings/SettingsPanel';
import OrgAdminPanel from '../components/orgadmin/OrgAdminPanel';
import ProgramIncrementsPanel from '../components/orgadmin/ProgramIncrementsPanel';
import TasksPanel from '../components/tasks/TasksPanel';

/**
 * Page chrome for a component that was written for a 320px column.
 *
 * Constrained rather than stretched: these are forms and lists, and a form
 * field run to 1600px is worse than one at a comfortable measure. The width
 * cap is what lets the same component serve both places without a rewrite.
 */
function PanelPage({
  title, description, children, wide = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 mb-4">{description}</p>
      )}
      <div className={`${wide ? 'max-w-4xl' : 'max-w-2xl'} mt-4`}>{children}</div>
    </div>
  );
}

export function SettingsPage() {
  return (
    <PanelPage title="Settings" description="Statuses, types and defaults">
      <SettingsPanel />
    </PanelPage>
  );
}

export function OrgAdminPage() {
  return (
    <PanelPage
      title="Organizations & Programs"
      description="Orgs, programs and release trains"
      wide
    >
      <OrgAdminPanel />
    </PanelPage>
  );
}

export function ProgramIncrementsPage() {
  return (
    <PanelPage
      title="Program Increments"
      description="PIs, sprints and cadence"
      wide
    >
      <ProgramIncrementsPanel />
    </PanelPage>
  );
}

export function TasksPage() {
  return (
    <PanelPage title="Tasks" description="Assigned work across projects" wide>
      <TasksPanel />
    </PanelPage>
  );
}
